/**
 * frontend/src/components/CategoryIcons.jsx
 * Line illustrations for the ten voucher categories.
 *
 * [Sprint UI-6] Deal-site category rows use small illustrated icons with
 * thin outlines rather than UI glyphs. These are drawn here on a 24px
 * grid, stroke 1.25, round caps — a little more scene than a lucide
 * icon (a cloche instead of cutlery, a teddy instead of a baby face) so
 * the row reads as "what you can get", not as a toolbar.
 */
import React from 'react';

function make(paths) {
  const Icon = ({ className = '', strokeWidth = 1.25, ...rest }) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...rest}
    >
      {paths}
    </svg>
  );
  return Icon;
}

// კვება — a cloche on a plate, a wisp of steam
export const FoodIcon = make(
  <>
    <path d="M4.5 15a7.5 7.5 0 0 1 15 0" />
    <path d="M2.5 15h19" />
    <path d="M4 18h16" />
    <circle cx="12" cy="6.5" r="1" />
    <path d="M12 7.5V9" />
    <path d="M17 4.5c0-1 1-1 1-2s-1-1-1-2" />
  </>
);

// სილამაზე — a lotus
export const BeautyIcon = make(
  <>
    <path d="M12 19c-2.6 0-4.5-2.2-4.5-5S12 6 12 6s4.5 5.2 4.5 8-1.9 5-4.5 5Z" />
    <path d="M7.5 13.5c-2.3-.3-4.2-1.7-5-3.5 2.4-.6 4.7 0 6.3 1.6" />
    <path d="M16.5 13.5c2.3-.3 4.2-1.7 5-3.5-2.4-.6-4.7 0-6.3 1.6" />
    <path d="M5 19c2 1.3 4.5 2 7 2s5-.7 7-2" />
  </>
);

// დასვენება — a beach umbrella under a sun
export const LeisureIcon = make(
  <>
    <path d="M3 13.5a8.5 8.5 0 0 1 17 0H3Z" />
    <path d="M7.5 13.5c0-4 1.8-8.3 4-8.3s4 4.3 4 8.3" />
    <path d="M11.5 13.5V21" />
    <path d="M8.5 21h6" />
    <circle cx="19.5" cy="4.5" r="1.8" />
    <path d="M19.5 1v.8M22 2.5l-.6.6M23 4.5h-.8" />
  </>
);

// გართობა — a party popper
export const FunIcon = make(
  <>
    <path d="M4.5 19.5 8 9.5l6.5 6.5-10 3.5Z" />
    <path d="M8 9.5c1.5 0 5 3.5 6.5 6.5" />
    <path d="M13 8c0-1.5 1-2.5 2.5-2.5S18 4.5 18 3" />
    <path d="M15.5 11c1.5 0 2.5 1 4 1s2-1 2-1" />
    <path d="M12 5.5V4" />
    <circle cx="19.5" cy="7" r=".6" fill="currentColor" stroke="none" />
    <circle cx="16" cy="14.5" r=".6" fill="currentColor" stroke="none" />
    <circle cx="21" cy="3" r=".6" fill="currentColor" stroke="none" />
  </>
);

// ჯანმრთელობა — a heart with a pulse trace
export const HealthIcon = make(
  <>
    <path d="M12 20.5 4.4 13a4.6 4.6 0 0 1 6.5-6.5L12 7.6l1.1-1.1a4.6 4.6 0 0 1 6.5 6.5L12 20.5Z" />
    <path d="M4.5 13h4l1.3-2.6 2.2 5 1.5-3.4H19" />
  </>
);

// ბავშვები — a teddy bear
export const KidsIcon = make(
  <>
    <circle cx="12" cy="11" r="6" />
    <circle cx="6.6" cy="6.2" r="2" />
    <circle cx="17.4" cy="6.2" r="2" />
    <circle cx="9.8" cy="10" r=".6" fill="currentColor" stroke="none" />
    <circle cx="14.2" cy="10" r=".6" fill="currentColor" stroke="none" />
    <path d="M10.3 13.2c.5.7 1 1 1.7 1s1.2-.3 1.7-1" />
    <path d="M12 12.4v1.8" />
    <path d="M7.5 21c.4-2.6 2.2-4 4.5-4s4.1 1.4 4.5 4" />
  </>
);

// ტექნიკა — a television on a stand
export const TechIcon = make(
  <>
    <rect x="3" y="4.5" width="18" height="12" rx="2" />
    <path d="M10.2 8.3v4.8l4-2.4-4-2.4Z" />
    <path d="M12 16.5v3" />
    <path d="M8 19.5h8" />
    <path d="M18 2.5 15.5 4.5M20.5 2.5 18 4.5" />
  </>
);

// აფთიაქი — a pill bottle with a cross
export const PharmacyIcon = make(
  <>
    <rect x="6" y="8" width="12" height="13" rx="2" />
    <path d="M8 8V5.5A1.5 1.5 0 0 1 9.5 4h5A1.5 1.5 0 0 1 16 5.5V8" />
    <path d="M12 11.5v6" />
    <path d="M9 14.5h6" />
  </>
);

// სუპერმარკეტი — a shopping basket
export const GroceryIcon = make(
  <>
    <path d="M3 10h18l-1.6 9a2 2 0 0 1-2 1.6H6.6a2 2 0 0 1-2-1.6L3 10Z" />
    <path d="M8 10l2.5-6" />
    <path d="M16 10l-2.5-6" />
    <path d="M9 13.5v3.5M12 13.5v3.5M15 13.5v3.5" />
  </>
);

// ავტო — a car, front view
export const AutoIcon = make(
  <>
    <path d="M4.5 12.5 6 8.3A2 2 0 0 1 7.9 7h8.2a2 2 0 0 1 1.9 1.3l1.5 4.2" />
    <path d="M3.5 13.5a1.5 1.5 0 0 1 1.5-1.5h14a1.5 1.5 0 0 1 1.5 1.5v3.5H3.5v-3.5Z" />
    <path d="M5.5 17v2M18.5 17v2" />
    <path d="M6.5 14.5h1.5M16 14.5h1.5" />
    <path d="M9.5 15h5" />
  </>
);
